import { routeParts, viewPaths, type AnyViewName, type Route, type ViewAnswer, type ViewName } from "@nabla/api";
import { readAnswer, type Pick, type Readout, type Schema } from "./answers.ts";
import { fillPath } from "./http.ts";
import type { Held } from "./http.ts";

export interface ViewRequest<T> {
  readonly key: string;
  readonly read: (held: Held) => Readout<T>;
}

type QueryValues = Readonly<Record<string, string | undefined>>;

type Builder<T> = (params?: Readonly<Record<string, string>>, query?: QueryValues) => ViewRequest<T>;

interface ModuleView<T> {
  readonly route: Route<"GET">;
  readonly answer: Schema<T>;
}

export function viewOf<K extends ViewName>(name: K): Builder<ViewAnswer<K>>;
export function viewOf<T>(view: ModuleView<T>): Builder<T>;
export function viewOf(named: ViewName | ModuleView<unknown>): Builder<unknown> {
  if (typeof named !== "string") return builder(named.route, () => named.answer);
  return builder(viewPaths[named].route, (api): Schema<unknown> => api.views[named].answer);
}

export function moduleViewOf<K extends AnyViewName>(name: K, route: Route<"GET">): Builder<ViewAnswer<K>>;
export function moduleViewOf(name: AnyViewName, route: Route<"GET">): Builder<unknown> {
  return builder(route, (api): Schema<unknown> => api.views[name].answer);
}

function builder<T>(route: Route<"GET">, pick: Pick<T>): Builder<T> {
  const memo = new WeakMap<object, Readout<T>>();
  const read = ({ body, sameApi }: Held): Readout<T> => {
    const known = memo.get(body);
    if (known) return known;
    const readout = readAnswer(body, sameApi, pick);
    memo.set(body, readout);
    return readout;
  };
  const { path } = routeParts(route);
  return (params = {}, query = {}) => ({ key: withQuery(fillPath(path, params), query), read });
}

function withQuery(path: string, query: QueryValues): string {
  const pairs = Object.entries(query).filter((pair): pair is [string, string] => pair[1] !== undefined);
  if (pairs.length === 0) return path;
  pairs.sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
  return `${path}?${new URLSearchParams(pairs).toString()}`;
}
